/**
 * Pure derivation of the Overview's Deliverables list.
 *
 * Turns the asset manifest (server-truth paths + mtimes) into three groups the
 * UI renders: files that have a tab, files that are download-only, and canonical
 * artifacts that don't exist yet. No React, no fetching — all inputs are passed
 * in so this is unit-testable in isolation.
 */

// Type-only, so this module still carries no React runtime dependency and stays
// testable without a renderer.
import type { ReactNode } from "react";
import {
  AdditionalAssetMeta,
  AssetManifestEntry,
  CANONICAL_ASSET_PATHS,
  TacoAssetType,
} from "@amzn/afxtaco-types";

/** Where a deliverable can be opened. */
export type DeliverableGroup = "inTab" | "downloadOnly" | "notGenerated";

export interface Deliverable {
  /** Project-relative path, e.g. `summary/summary.md`. Absent when not generated. */
  path?: string;
  /** Human label, e.g. "Summary" or "Market Research". */
  label: string;
  /** ISO-8601 mtime from the manifest, when the file exists. */
  lastModified?: string;
  group: DeliverableGroup;
  /**
   * The tab value to activate on click. Undefined for download-only and
   * not-yet-generated rows.
   */
  tabValue?: string;
  /**
   * Upper-cased file type shown as a badge, e.g. "MD" or "CSV". Set for every row
   * that has a path — it replaced the raw path as the type indicator, which was
   * long, repetitive, and told the user nothing they could act on.
   */
  extension?: string;
  /**
   * The glyph its tab uses, so a row is recognisable as the same thing as the
   * tab it opens. Supplied by the caller (the page owns tab identity); rows with
   * no dedicated tab fall back to a generic file icon in the card.
   */
  icon?: ReactNode;
}

/**
 * Canonical artifacts that have a dedicated tab, in the order the tab bar shows
 * them. Drives both the "in a tab" rows and the "not generated yet" placeholders,
 * so a project missing its diagram says so instead of silently omitting it.
 *
 * `tabValue` is passed in by the caller rather than hardcoded here, because the
 * tab identifiers live in the page component and some are feature-flagged.
 */
export interface CanonicalDeliverableSpec {
  assetType: TacoAssetType;
  label: string;
  tabValue: string;
  /** The glyph this artifact's tab renders, passed through onto its row. */
  icon?: ReactNode;
}

/**
 * Upper-cased extension of a path, or undefined when it has none.
 *
 * Only looks after the last `/`, so a dot in a directory name can't be mistaken
 * for an extension.
 */
export function extensionOf(path: string): string | undefined {
  const filename = path.slice(path.lastIndexOf("/") + 1);
  const dot = filename.lastIndexOf(".");
  if (dot <= 0 || dot === filename.length - 1) return undefined;
  return filename.slice(dot + 1).toUpperCase();
}

/** Paths that exist for the agent's benefit and are never user-facing rows. */
const INTERNAL_PATH_PREFIXES: readonly string[] = [
  "thinking/",
  "diagram_baseline/",
  "indexed/",
  "uploaded_files/",
  // The structured delivery definition. Data behind the checklist card, not a
  // deliverable, and it is JSON so it would otherwise show as download-only.
  "delivery/",
];

const isInternalPath = (path: string): boolean =>
  INTERNAL_PATH_PREFIXES.some((prefix) => path.startsWith(prefix));

/**
 * Groups a project's assets into the three Deliverables buckets.
 *
 * @param manifest - every current file under the project, with mtimes
 * @param additionalAssets - the non-canonical subset, carrying `isRenderable`
 * @param canonicalSpecs - canonical artifacts to show, with their tab values
 * @param additionalLabel - maps a filename to a display label
 *   (pass `additionalAssetLabel`)
 * @param additionalTabValue - maps a path to its additional-asset tab value
 * @returns the deliverables, ordered: in-tab, download-only, then not-generated
 */
export function buildDeliverables({
  manifest,
  additionalAssets,
  canonicalSpecs,
  additionalLabel,
  additionalTabValue,
}: {
  manifest: AssetManifestEntry[];
  additionalAssets: AdditionalAssetMeta[];
  canonicalSpecs: CanonicalDeliverableSpec[];
  additionalLabel: (filename: string) => string;
  additionalTabValue: (path: string) => string;
}): Deliverable[] {
  const mtimeByPath = new Map(manifest.map((e) => [e.path, e.lastModified]));

  const inTab: Deliverable[] = [];
  const notGenerated: Deliverable[] = [];

  for (const spec of canonicalSpecs) {
    const path = CANONICAL_ASSET_PATHS[spec.assetType];
    const lastModified = mtimeByPath.get(path);

    const shared = {
      path,
      label: spec.label,
      tabValue: spec.tabValue,
      ...(spec.icon ? { icon: spec.icon } : {}),
      ...(extensionOf(path) ? { extension: extensionOf(path) } : {}),
    };

    if (lastModified) {
      inTab.push({ ...shared, lastModified, group: "inTab" });
    } else {
      notGenerated.push({ ...shared, group: "notGenerated" });
    }
  }

  const downloadOnly: Deliverable[] = [];

  for (const asset of additionalAssets) {
    if (isInternalPath(asset.path)) continue;

    const lastModified = asset.lastModified ?? mtimeByPath.get(asset.path);
    const extension =
      asset.extension?.toUpperCase() || extensionOf(asset.path) || undefined;
    const row: Deliverable = {
      path: asset.path,
      label: additionalLabel(asset.filename),
      ...(lastModified ? { lastModified } : {}),
      group: asset.isRenderable ? "inTab" : "downloadOnly",
      // Type badge on both groups now, not just download-only: it replaced the
      // path line, so every row needs it to say what the file is. Prefer the
      // manifest's extension, falling back to the path for entries without one.
      ...(extension ? { extension } : {}),
      ...(asset.isRenderable
        ? { tabValue: additionalTabValue(asset.path) }
        : {}),
    };

    if (asset.isRenderable) {
      inTab.push(row);
    } else {
      downloadOnly.push(row);
    }
  }

  // Newest first within each group; rows without a timestamp sort last.
  const byRecency = (a: Deliverable, b: Deliverable) =>
    (b.lastModified ?? "").localeCompare(a.lastModified ?? "");

  return [
    ...inTab.sort(byRecency),
    ...downloadOnly.sort(byRecency),
    ...notGenerated,
  ];
}

/** Deliverables that actually exist in S3. */
export const generatedDeliverables = (rows: Deliverable[]): Deliverable[] =>
  rows.filter((r) => r.group !== "notGenerated");

/**
 * How many deliverables changed within the last 24 hours.
 *
 * @param rows - the built deliverables
 * @param now - injectable current time, for tests
 */
export function countChangedToday(
  rows: Deliverable[],
  now: number = Date.now(),
): number {
  const dayAgo = now - 24 * 60 * 60 * 1000;
  return rows.filter((r) => {
    if (!r.lastModified) return false;
    const ms = new Date(r.lastModified).getTime();
    return !Number.isNaN(ms) && ms >= dayAgo;
  }).length;
}
